import { useEffect, useState } from 'react'

/**
 * A counter that increments each time this page is returned to, for keying the entrance animation.
 *
 * The animation is CSS, so it plays on mount and never again — but closing an activity does not
 * remount anything. It pops history, and this document comes back either from the back/forward cache
 * or from having been backgrounded while the activity held the screen. Feeding this into the cards'
 * keys remounts them, which is what replays the animation.
 *
 * Two signals: `pageshow` for a cached restore, and `visibilitychange` for when the activity was a
 * separate browsing context and this document was only ever backgrounded.
 *
 */
export default function useEnterAnimationRun(enabled) {
  const [run, setRun] = useState(0)

  useEffect(() => {
    if (!enabled) return undefined

    const replay = () => setRun((current) => current + 1)
    const replayOnRestore = (event) => {
      if (event.persisted) replay()
    }
    const replayOnVisible = () => {
      if (document.visibilityState === 'visible') replay()
    }

    window.addEventListener('pageshow', replayOnRestore)
    document.addEventListener('visibilitychange', replayOnVisible)

    return () => {
      window.removeEventListener('pageshow', replayOnRestore)
      document.removeEventListener('visibilitychange', replayOnVisible)
    }
  }, [enabled])

  return run
}
